import { Suspense, useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { TextFieldElement } from 'components/muiInputWrappers';
import { FormItem, FormTitle, GenericModalV2 } from 'components/shared';

import { styles } from './constants';
import useGetTicketDetail from './hooks/useGetTicketDetail';
import useGetWorkFlowOrderDetail from './hooks/useGetWorkFlowOrderDetail';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  ticketId: number;
  workFlowOrderId: number;
}

function TicketInfoModal({
  isOpen,
  onClose,
  ticketId,
  workFlowOrderId
}: Props) {
  const { t } = useTranslation('global');
  const form = useForm({
    mode: 'onSubmit'
  });

  const navigate = useNavigate();

  const getTicketDetail = useGetTicketDetail(ticketId, () => {});
  const getWorkFlowOrderDetail = useGetWorkFlowOrderDetail(workFlowOrderId);

  useEffect(() => {
    if (getTicketDetail.isSuccess && getWorkFlowOrderDetail.isSuccess) {
      form.setValue(
        'ticketClientGroup',
        getTicketDetail?.data?.ticketClientGroup
      );
      form.setValue('workFlowName', getWorkFlowOrderDetail?.data?.workFlowName);
      form.setValue('serviceName', getTicketDetail?.data?.ticketServiceName);
      form.setValue(
        'assignedEngineerFullName',
        getTicketDetail?.data?.ticketAssignedEngineerFullName
      );
      form.setValue('statusName', getTicketDetail?.data?.ticketStatusName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketDetail.isSuccess, getWorkFlowOrderDetail.isSuccess]);

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={() => {
        getTicketDetail.remove();
        getWorkFlowOrderDetail.remove();
        onClose();
      }}
      style={styles.serviceModal}
      title={t('ticket.modalGantInfo.IdTicket', {
        ticketId
      })}
    >
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </div>
        }
      >
        <Grid spacing={2} xs={12} container item>
          {(getTicketDetail.isLoading || getWorkFlowOrderDetail.isLoading) && (
            <Grid xs={12} item>
              <LinearProgress />
            </Grid>
          )}
          <Grid xs={12} item>
            <FormTitle variant="body2" gutterBottom>
              {t('ticket.modalGantInfo.enabligService')}
            </FormTitle>
            <FormItem>
              <TextFieldElement
                control={form.control}
                name="ticketClientGroup"
                size="small"
                type="text"
                variant="outlined"
                disabled
                fullWidth
              />
            </FormItem>
          </Grid>
          <Grid xs={12} item>
            <FormTitle variant="body2" gutterBottom>
              {t('ticket.modalGantInfo.enabligServiceFlow')}
            </FormTitle>
            <FormItem>
              <TextFieldElement
                control={form.control}
                name="workFlowName"
                size="small"
                type="text"
                variant="outlined"
                disabled
                fullWidth
              />
            </FormItem>
          </Grid>
          <Grid xs={12} item>
            <FormTitle variant="body2" gutterBottom>
              {t('ticket.modalGantInfo.service')}
            </FormTitle>
            <FormItem>
              <TextFieldElement
                control={form.control}
                name="serviceName"
                size="small"
                type="text"
                variant="outlined"
                disabled
                fullWidth
              />
            </FormItem>
          </Grid>
          <Grid xs={12} item>
            <FormTitle variant="body2" gutterBottom>
              {t('ticket.modalGantInfo.responsible')}
            </FormTitle>
            <FormItem>
              <TextFieldElement
                control={form.control}
                name="assignedEngineerFullName"
                size="small"
                type="text"
                variant="outlined"
                disabled
                fullWidth
              />
            </FormItem>
          </Grid>
          <Grid xs={12} item>
            <FormTitle variant="body2" gutterBottom>
              {t('ticket.modalGantInfo.incidentStatus')}
            </FormTitle>
            <FormItem>
              <TextFieldElement
                control={form.control}
                name="statusName"
                size="small"
                type="text"
                variant="outlined"
                disabled
                fullWidth
              />
            </FormItem>
          </Grid>
          {/* centro el boton */}
          <Grid
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '1rem'
            }}
            xs={12}
            item
          >
            <Button
              color="primary"
              onClick={() =>
                navigate(`/requirement/detail/${ticketId}/general`)
              }
              variant="contained"
              fullWidth
            >
              {t('ticket.modalGantInfo.detail')}
            </Button>
          </Grid>
        </Grid>
      </Suspense>
    </GenericModalV2>
  );
}

export default TicketInfoModal;
